import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { RndcMinistryCargoListOptions } from 'src/app/core/interfaces/rndcCargoList';
import { RndcMinistryEntity, RndcMinistryErrorField } from 'src/app/core/interfaces/rndcMinistry';
import { RndcRetryState } from 'src/app/core/interfaces/rndcRetry';
import { MinistryRetryRetransmitMessages, RndcEntityLabel, RndcEntityRetransmitMessages, RndcRetransmitEvaluation, RndcRetransmitFeedback, RndcRetransmitOutcome, RndcRetransmitSnackBarType } from 'src/app/core/interfaces/rndcRetransmit';
import { CargoStateEnum } from 'src/app/core/enums/cargoState.enum';
export {
  RndcMinistryEntity,
  RndcMinistryErrorField,
} from 'src/app/core/interfaces/rndcMinistry';
export { RndcRetryState } from 'src/app/core/interfaces/rndcRetry';
export { MinistryRetryRetransmitMessages } from 'src/app/core/interfaces/rndcRetransmit';
export { RndcMinistryCargoListOptions } from 'src/app/core/interfaces/rndcCargoList';

export function filterRndcRowsByText<T>(
  rows: T[],
  filter: string,
  getSearchableValue: (row: T) => string
): T[] {
  if (!rows.length) {
    return [];
  }
  const normalizedFilter = filter ? filter.trim().toUpperCase() : '';
  if (!normalizedFilter) {
    return rows;
  }
  return rows.filter((row) => {
    const searchableValue = getSearchableValue(row);
    return searchableValue && searchableValue.toUpperCase().includes(normalizedFilter);
  });
}

export function normalizeRndcPlateFilter(value: string): string {
  return value ? value.trim().toUpperCase() : '';
}

export const RNDC_STATUS_MESSAGES = {
  pending: 'Falta Retransmitir',
  manual: 'Es obligatorio realizar correcciones para retransmitir',
  fallback: 'Es necesario realizar ajuste manual',
  retryFallback: 'Es necesario retransmitir al RNDC.',
  none: '-'
} as const;

export const RNDC_RETRANSMIT_MANUAL_DIALOG_DESCRIPTION =
  'Antes de retransmitir es necesario realizar el ajuste manual';

export const RNDC_RETRANSMIT_CONTINGENCY_BLOCKED_TOOLTIP =
  'No se puede retransmitir mientras el plan de contingencia RNDC esté activo';

export const RNDC_RETRANSMIT_CONTINGENCY_BLOCKED_SNACKBAR =
  'No se puede retransmitir mientras el plan de contingencia RNDC esté activo.';

export const RNDC_CANCEL_MANIFEST_CONTINGENCY_BLOCKED_TOOLTIP =
  'No se puede anular el manifiesto mientras el RNDC esta en contingencia';

export const RNDC_CANCEL_MANIFEST_CONTINGENCY_BLOCKED_SNACKBAR =
  'No se puede anular el manifiesto mientras el RNDC esta en contingencia.';

export function shouldShowRndcRetransmitConfirmationDialog(retryState: RndcRetryState): boolean {
  return retryState === 'manual';
}

const RNDC_CONTINGENCY_CARGO_STATES: CargoStateEnum[] = [
  CargoStateEnum.REQUEST,
  CargoStateEnum.CREATED,
  CargoStateEnum.ACCEPTED,
  CargoStateEnum.START_SERVICE,
];

const RNDC_FULFILLMENT_CARGO_STATES: CargoStateEnum[] = [
  CargoStateEnum.END_SERVICE,
];

interface MinistryRetryTypeConfig {
  readonly label: string;
  readonly entityPhrase: string;
  readonly retryFallback: string;
  readonly retransmitMessages: MinistryRetryRetransmitMessages;
}

const DEFAULT_MINISTRY_RETRY_TYPE_CONFIG: MinistryRetryTypeConfig = {
  label: 'Registro',
  entityPhrase: 'el registro',
  retryFallback: RNDC_STATUS_MESSAGES.retryFallback,
  retransmitMessages: {
    insufficientInfo: 'No hay información suficiente para retransmitir el registro.',
    success: 'Registro retransmitido correctamente.',
    error: 'No fue posible retransmitir el registro.',
  },
};

const MINISTRY_RETRY_TYPE_CONFIG: Record<string, MinistryRetryTypeConfig> = {
  consignment: {
    label: 'Remesa',
    entityPhrase: 'la remesa',
    retryFallback: 'Es necesario retransmitir la remesa al RNDC.',
    retransmitMessages: {
      insufficientInfo: 'No hay información suficiente para retransmitir la remesa.',
      success: 'Remesa retransmitida correctamente.',
      error: 'No fue posible retransmitir la remesa.',
    },
  },
  manifest: {
    label: 'Manifiesto',
    entityPhrase: 'el manifiesto',
    retryFallback: 'Es necesario retransmitir el manifiesto al RNDC.',
    retransmitMessages: {
      insufficientInfo: 'No hay información suficiente para retransmitir el manifiesto.',
      success: 'Manifiesto retransmitido correctamente.',
      error: 'No fue posible retransmitir el manifiesto.',
    },
  },
  fulfillConsignment: {
    label: 'Cumplido',
    entityPhrase: 'el cumplido',
    retryFallback: 'Es necesario retransmitir el cumplido al RNDC.',
    retransmitMessages: {
      insufficientInfo: 'No hay información suficiente para retransmitir el cumplido.',
      success: 'Cumplido retransmitido correctamente.',
      error: 'No fue posible retransmitir el cumplido.',
    },
  },
};

function resolveMinistryRetryTypeConfig(type: string | null | undefined): MinistryRetryTypeConfig {
  if (!type) {
    return DEFAULT_MINISTRY_RETRY_TYPE_CONFIG;
  }
  return MINISTRY_RETRY_TYPE_CONFIG[type] || DEFAULT_MINISTRY_RETRY_TYPE_CONFIG;
}

export function getMinistryRetryTypeLabel(type: string | null | undefined): string {
  if (!type) {
    return '-';
  }
  const config = MINISTRY_RETRY_TYPE_CONFIG[type];
  return config ? config.label : type;
}

export function getMinistryRetryTypeMessage(type: string | null | undefined): string {
  const label = getMinistryRetryTypeLabel(type);
  if (label === '-') {
    return '-';
  }
  return `Pendiente de corrección en ${label.toLowerCase()}`;
}


export function resolveMinistryRetryTypeForAction(
  viewFilter: string | null,
  rowType: string | null | undefined
): string | null {
  return viewFilter || rowType || null;
}


export function getMinistryRetryRetransmitMessages(
  type: string | null | undefined
): MinistryRetryRetransmitMessages {
  return resolveMinistryRetryTypeConfig(type).retransmitMessages;
}

export function parseRndcCargoConsecutive(consecutive: string | null | undefined): number | null {
  if (!consecutive || consecutive === '-') {
    return null;
  }
  const parsed = Number(consecutive.trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function resolveCargoStatesByMinistryType(type: string | null): CargoStateEnum[] {
  if (type === 'fulfillConsignment') {
    return RNDC_FULFILLMENT_CARGO_STATES;
  }

  if (type === null) {
    return [...RNDC_CONTINGENCY_CARGO_STATES, ...RNDC_FULFILLMENT_CARGO_STATES];
  }
  return RNDC_CONTINGENCY_CARGO_STATES;
}

export function getRndcStatusMessage(entity: RndcMinistryEntity): string {
  if (entity && entity.ministryRetry) {
    return RNDC_STATUS_MESSAGES.pending;
  }
  if (entity && entity.ministryManualAdjustment) {
    return RNDC_STATUS_MESSAGES.manual;
  }
  return RNDC_STATUS_MESSAGES.none;
}

const RNDC_STATUS_STYLES: Record<string, { cssClass: string; iconClass: string }> = {
  [RNDC_STATUS_MESSAGES.pending]: {
    cssClass: 'rndc-error-block--pending',
    iconClass: 'fa-hourglass-half',
  },
  [RNDC_STATUS_MESSAGES.manual]: {
    cssClass: 'rndc-error-block--fault',
    iconClass: 'fa-exclamation-triangle',
  },
};

export function getRndcStatusCssClass(statusMessage: string): string {
  const style = RNDC_STATUS_STYLES[statusMessage];
  return style ? style.cssClass : 'rndc-error-block--neutral';
}

export function getRndcStatusIconCssClass(statusMessage: string): string {
  const style = RNDC_STATUS_STYLES[statusMessage];
  return style ? style.iconClass : 'fa-info-circle';
}

function getTrimmedRndcErrorText(errorField: { error?: string } | undefined): string {
  return errorField && errorField.error ? errorField.error.trim() : '';
}

function getMinistryErrorText(ministryError: RndcMinistryErrorField | undefined): string {
  if (!ministryError || typeof ministryError === 'boolean') {
    return '';
  }
  return ministryError.error ? ministryError.error.trim() : '';
}

function getRndcRetryFallbackMessage(type?: string | null): string {
  if (!type) {
    return RNDC_STATUS_MESSAGES.retryFallback;
  }
  const config = MINISTRY_RETRY_TYPE_CONFIG[type];
  return config ? config.retryFallback : RNDC_STATUS_MESSAGES.retryFallback;
}

function getRndcConcreteMinistryError(entity: RndcMinistryEntity | null | undefined): string {
  if (!entity) {
    return '';
  }
  const reason = entity.ministryManualAdjustmentReason ? entity.ministryManualAdjustmentReason.trim() : '';
  if (reason) {
    return reason;
  }
  const rndcError = getTrimmedRndcErrorText(entity.errorRNDC);
  if (rndcError) {
    return rndcError;
  }
  const rndLegacyError = getTrimmedRndcErrorText(entity.errorRND);
  if (rndLegacyError) {
    return rndLegacyError;
  }
  const manifestError = getTrimmedRndcErrorText(entity.manifestError);
  if (manifestError) {
    return manifestError;
  }
  return getMinistryErrorText(entity.ministryError);
}

export function getRndcMinistryError(entity: RndcMinistryEntity): string {
  const concreteError = getRndcConcreteMinistryError(entity);
  if (concreteError) {
    return concreteError;
  }
  if (entity && entity.ministryManualAdjustment) {
    return RNDC_STATUS_MESSAGES.fallback;
  }
  if (entity && entity.ministryRetry) {
    return getRndcRetryFallbackMessage(entity.ministryRetryType);
  }
  return '';
}

export function hasRndcMinistryManualReason(entity: RndcMinistryEntity): boolean {
  if (!entity) {
    return false;
  }
  if (getRndcConcreteMinistryError(entity)) {
    return true;
  }
  return entity.ministryManualAdjustment === true;
}

const RETRANSMIT_SNACK_BAR_BY_OUTCOME: Record<RndcRetransmitOutcome, RndcRetransmitSnackBarType> = {
  success: 'success',
  error: 'error',
  warning: 'alert',
};

const ENTITY_RETRANSMIT_LABELS: Record<RndcEntityLabel, string> = {
  vehículo: 'Vehículo',
  usuario: 'Usuario',
  trailer: 'Trailer',
  sede: 'Sede',
};

const ENTITY_RETRANSMIT_PHRASE: Record<RndcEntityLabel, string> = {
  vehículo: 'el vehículo',
  usuario: 'el usuario',
  trailer: 'el trailer',
  sede: 'la sede',
};

const ENTITY_RETRANSMIT_SUCCESS_PARTICIPLE: Record<RndcEntityLabel, string> = {
  vehículo: 'retransmitido',
  usuario: 'retransmitido',
  trailer: 'retransmitido',
  sede: 'retransmitida',
};

function buildRetransmitEvaluation(
  outcome: RndcRetransmitOutcome,
  message: string
): RndcRetransmitEvaluation {
  return { outcome, message, shouldReloadList: true };
}


export function getEntityRetransmitMessages(entityLabel: RndcEntityLabel): RndcEntityRetransmitMessages {
  const capitalizedLabel = ENTITY_RETRANSMIT_LABELS[entityLabel];
  const entityPhrase = ENTITY_RETRANSMIT_PHRASE[entityLabel];
  return {
    success: `${capitalizedLabel} ${ENTITY_RETRANSMIT_SUCCESS_PARTICIPLE[entityLabel]} correctamente.`,
    pending: `La solicitud se envió al RNDC, pero ${entityPhrase} sigue pendiente de retransmisión.`,
    manualRequired: `El RNDC requiere ajuste manual para ${entityPhrase} antes de completar la retransmisión.`,
    unconfirmed: `No se pudo confirmar la retransmisión de ${entityPhrase}.`,
    httpError: `No fue posible retransmitir ${entityPhrase}.`,
  };
}


function getRndcRetransmitBlockingError(entity: RndcMinistryEntity): string {
  return getTrimmedRndcErrorText(entity.errorRNDC)
    || getTrimmedRndcErrorText(entity.errorRND)
    || getTrimmedRndcErrorText(entity.manifestError)
    || getMinistryErrorText(entity.ministryError);
}

function wrapRetransmitEvaluation(evaluation: RndcRetransmitEvaluation): RndcRetransmitFeedback {
  return {
    ...evaluation,
    snackBarType: RETRANSMIT_SNACK_BAR_BY_OUTCOME[evaluation.outcome],
  };
}

function getManualAdjustmentDetail(entity: RndcMinistryEntity, fallbackMessage: string): string {
  const reason = entity.ministryManualAdjustmentReason ? entity.ministryManualAdjustmentReason.trim() : '';
  return reason || fallbackMessage;
}

function evaluateEntityRetransmitByState(
  entity: RndcMinistryEntity,
  retryState: RndcRetryState,
  messages: RndcEntityRetransmitMessages
): RndcRetransmitEvaluation {
  if (retryState === 'manual') {
    if (entity.ministryManualAdjustment === true) {
      return buildRetransmitEvaluation(
        'warning',
        getRndcMinistryError(entity) || messages.manualRequired
      );
    }
    return buildRetransmitEvaluation('success', messages.success);
  }

  if (entity.ministryManualAdjustment === true) {
    return buildRetransmitEvaluation(
      'warning',
      getManualAdjustmentDetail(entity, messages.manualRequired)
    );
  }
  if (entity.ministryRetry === true) {
    return buildRetransmitEvaluation('warning', messages.pending);
  }
  if (entity.ministryRetry === false) {
    return buildRetransmitEvaluation('success', messages.success);
  }
  return buildRetransmitEvaluation('warning', messages.unconfirmed);
}

function evaluateRndcEntityRetransmitOutcome(
  entity: RndcMinistryEntity | null | undefined,
  retryState: RndcRetryState,
  messages: RndcEntityRetransmitMessages
): RndcRetransmitEvaluation {
  if (!entity) {
    return buildRetransmitEvaluation('warning', messages.unconfirmed);
  }
  const blockingError = getRndcRetransmitBlockingError(entity);
  if (blockingError) {
    return buildRetransmitEvaluation('error', blockingError);
  }
  return evaluateEntityRetransmitByState(entity, retryState, messages);
}

export function getCargoRetransmitMessages(
  retryType: string | null | undefined
): RndcEntityRetransmitMessages {
  const ministryConfig = resolveMinistryRetryTypeConfig(retryType);
  const ministryMessages = ministryConfig.retransmitMessages;
  const entityPhrase = ministryConfig.entityPhrase;
  return {
    success: ministryMessages.success,
    pending: `La solicitud se envió al RNDC, pero ${entityPhrase} sigue pendiente de retransmisión.`,
    manualRequired: `El RNDC requiere ajuste manual para ${entityPhrase} antes de completar la retransmisión.`,
    unconfirmed: `No se pudo confirmar la retransmisión de ${entityPhrase}.`,
    httpError: ministryMessages.error,
  };
}

export function buildRndcRetransmitFeedback(
  entity: RndcMinistryEntity | null | undefined,
  retryState: RndcRetryState,
  entityLabel: RndcEntityLabel
): RndcRetransmitFeedback {
  const messages = getEntityRetransmitMessages(entityLabel);
  const evaluation = evaluateRndcEntityRetransmitOutcome(entity, retryState, messages);
  return wrapRetransmitEvaluation(evaluation);
}

export function buildCargoRetransmitFeedback(
  cargo: (RndcMinistryEntity & { manifest?: string }) | null | undefined,
  retryState: RndcRetryState,
  retryType: string | null | undefined
): RndcRetransmitFeedback {
  const messages = getCargoRetransmitMessages(retryType);
  const evaluation = evaluateRndcEntityRetransmitOutcome(cargo, retryState, messages);
  if (retryType === 'manifest' && retryState === 'retry' && evaluation.outcome === 'success' && cargo && !cargo.manifest) {
    return wrapRetransmitEvaluation(buildRetransmitEvaluation('warning', messages.unconfirmed));
  }
  return wrapRetransmitEvaluation(evaluation);
}

export function buildBranchRetransmitFeedback(
  address: RndcMinistryEntity | null | undefined,
  retryState: RndcRetryState
): RndcRetransmitFeedback {
  return buildRndcRetransmitFeedback(address, retryState, 'sede');
}

function isRndcMinistryEntityShape(value: unknown): value is RndcMinistryEntity {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const entity = value as RndcMinistryEntity;
  return entity.ministryRetry !== undefined
    || entity.ministryManualAdjustment !== undefined
    || !!(entity.ministryManualAdjustmentReason && entity.ministryManualAdjustmentReason.trim())
    || !!entity.errorRNDC
    || !!entity.errorRND
    || !!entity.manifestError
    || entity.ministryError !== undefined;
}

export function extractRndcMinistryEntityFromHttpError(httpError: unknown): RndcMinistryEntity | null {
  if (!httpError || typeof httpError !== 'object') {
    return null;
  }
  const responseBody = (httpError as { error?: unknown }).error;
  if (!responseBody || typeof responseBody !== 'object') {
    return null;
  }
  const nestedEntity = (responseBody as { error?: unknown }).error;
  if (isRndcMinistryEntityShape(nestedEntity)) {
    return nestedEntity;
  }
  if (isRndcMinistryEntityShape(responseBody)) {
    return responseBody as RndcMinistryEntity;
  }
  return null;
}

export function shouldReloadRndcListAfterRetransmit(feedback: RndcRetransmitFeedback): boolean {
  return feedback.shouldReloadList && feedback.outcome === 'success';
}


export function getContingencyMessage(
  entity: RndcMinistryEntity,
  entityLabel: string
): string {
  if (entity && entity.ministryContingency === true) {
    return `${entityLabel} creado en contingencia`;
  }
  return '';
}

/** Ministry flags used to decide whether the intermittency authorization dialog should open. */
export interface RndcIntermittencyDialogFlags {
  ministryContingency?: boolean | null;
  ministryRetry?: boolean | null;
}

interface RndcValidateErrorBody extends RndcIntermittencyDialogFlags {
  message?: string;
  id?: string;
}

function getRndcResponseBody(responseOrError: unknown): RndcValidateErrorBody | null {
  if (!responseOrError || typeof responseOrError !== 'object') {
    return null;
  }
  const response = responseOrError as { error?: RndcValidateErrorBody };
  if (response.error && typeof response.error === 'object') {
    return response.error;
  }
  if (responseOrError instanceof HttpErrorResponse) {
    return null;
  }
  return responseOrError as RndcValidateErrorBody;
}

function getRndcValidateErrorBody(responseOrError: unknown): RndcValidateErrorBody | null {
  return getRndcResponseBody(responseOrError);
}

export function isCargoRndcResponseBody(body: unknown): body is Cargo {
  if (!body || typeof body !== 'object') {
    return false;
  }
  const candidate = body as { id?: unknown };
  return typeof candidate.id === 'string' && candidate.id.trim().length > 0;
}

/** Reads the Cargo object returned by validate or start-service (body or error.error). */
export function extractCargoFromRndcResponse(responseOrError: unknown): Cargo | null {
  const body = getRndcResponseBody(responseOrError);
  return isCargoRndcResponseBody(body) ? body : null;
}

/** Merges the cargo from the HTTP response into the cargo already loaded in memory. */
export function mergeCargoFromRndcResponse(
  currentCargo: Cargo | undefined,
  responseOrError: unknown
): Cargo | undefined {
  const responseCargo = extractCargoFromRndcResponse(responseOrError);
  if (!responseCargo) {
    return currentCargo;
  }
  if (!currentCargo) {
    return responseCargo;
  }
  return Object.assign({}, currentCargo, responseCargo);
}

function getRndcValidateErrorMessage(responseOrError: unknown): string {
  const errorBody = getRndcValidateErrorBody(responseOrError);
  const message = errorBody ? errorBody.message : undefined;
  return typeof message === 'string' ? message.trim() : '';
}

export function isRndcContingencyMode(ministryContingency: boolean | null | undefined): boolean {
  return ministryContingency != null;
}

/**
 * Resolves ministry flags from the HTTP response body (Cargo) or the cargo in memory.
 * The response body takes precedence when a flag is present.
 */
export function resolveRndcIntermittencyFlags(
  responseOrError: unknown,
  cargo?: RndcIntermittencyDialogFlags
): RndcIntermittencyDialogFlags {
  const errorBody = getRndcValidateErrorBody(responseOrError);
  const errorMinistryContingency = errorBody ? errorBody.ministryContingency : undefined;
  const cargoMinistryContingency = cargo ? cargo.ministryContingency : undefined;
  const errorMinistryRetry = errorBody ? errorBody.ministryRetry : undefined;
  const cargoMinistryRetry = cargo ? cargo.ministryRetry : undefined;

  return {
    ministryContingency: errorMinistryContingency != null
      ? errorMinistryContingency
      : cargoMinistryContingency,
    ministryRetry: errorMinistryRetry != null ? errorMinistryRetry : cargoMinistryRetry,
  };
}

/** User-facing message from the validate/start-service HTTP error body. */
export function getRndcRequestFailureDisplayMessage(responseOrError: unknown): string | undefined {
  const httpMessage = getRndcValidateErrorMessage(responseOrError);
  return httpMessage || undefined;
}

/**
 * Decides if the intermittency authorization dialog should open.
 * Contingency (ministryContingency defined): never.
 * Intermittency: only when ministryRetry is true on the cargo or HTTP error body.
 */
export function shouldShowRndcIntermittencyAuthorizationDialog(
  responseOrError: unknown,
  cargo?: RndcIntermittencyDialogFlags
): boolean {
  const ministryFlags = resolveRndcIntermittencyFlags(responseOrError, cargo);

  if (isRndcContingencyMode(ministryFlags.ministryContingency)) {
    return false;
  }

  return ministryFlags.ministryRetry === true;
}

function sanitizeRndcFilterQuery(customFilters: string): string {
  if (!customFilters || !customFilters.trim()) {
    return '';
  }
  const excludedKeys = ['state', 'states', 'tripTypeName', 'productTypeName', 'isEqualProductType'];
  const normalized = customFilters.startsWith('&') ? customFilters.slice(1) : customFilters;
  return normalized
    .split('&')
    .filter((param) => {
      const key = param.split('=')[0];
      return key && !excludedKeys.includes(key);
    })
    .join('&');
}

export function loadMinistryRetryCargos(options: RndcMinistryCargoListOptions): Observable<Cargo[]> {
  const { paging, retryState, ministryRetryType, customFilters, cargoService } = options;
  const sanitized = sanitizeRndcFilterQuery(customFilters);
  const baseFlag =
    retryState === 'retry' ? 'ministryRetry=true' : 'ministryManualAdjustment=true';
  const query = buildMinistryQuery(baseFlag, ministryRetryType, sanitized);
  const states = resolveCargoStatesByMinistryType(ministryRetryType);
  return cargoService.cargoList({ ...paging, states }, query).pipe(
    map((response) => (response && response.results) || []),
    catchError(() => of([] as Cargo[]))
  );
}

function buildMinistryQuery(
  baseFlag: string,
  ministryRetryType: string | null,
  sanitizedFilters: string
): string {
  const withType = ministryRetryType
    ? `${baseFlag}&ministryRetryType=${ministryRetryType}`
    : baseFlag;
  return sanitizedFilters ? `${withType}&${sanitizedFilters}` : withType;
}
